import { Component, computed, effect, linkedSignal, OnInit, signal } from '@angular/core';
import { TuiDialogContext, TuiScrollbar } from '@taiga-ui/core';
import { injectContext } from '@taiga-ui/polymorpheus';
import { filter, map } from 'rxjs';
import { Result } from 'ts-results';
import { Prettify } from 'src/app/shared/features/esk-tool/util';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TuiCheckbox } from '@taiga-ui/kit';
import { CustomValidators } from 'src/app/shared/extensions/CustomValidators';
import { LayerDTO } from '../data-access/feature-layers.service';
import { layerExportTypes } from '../data-access/state';
import { ExtendedLayerDTO } from './create-scenario/create-scenario.component';
import { signalSlice } from 'ngxtension/signal-slice';
import { formValues } from './download-layer-dialog/download-layer-dialog.component';
import {TuiDataList, TuiDropdown, TuiIcon, TuiTextfield} from '@taiga-ui/core';
import {TuiChevron, TuiDataListDropdownManager, TuiSelect} from '@taiga-ui/kit';
import { TuiStringHandler } from '@taiga-ui/cdk';

@Component({
  selector: 'export-layer-dialog',
  imports: [ReactiveFormsModule, 	        TuiChevron,
    TuiDataList,
    TuiSelect,
    TuiTextfield,],
  template: `


  <form [formGroup]="form">
  <p class="mb-4 text-gray-600">
      Select your preferred format and coordinate system for exporting this layer.
      The exported file will contain all features with their attributes.
    </p>

    <div class="mb-6">
      <label class="block text-sm font-medium mb-1">Export Format</label>
      <p class="text-xs text-gray-500 mb-2">
        Choose between CSV (spreadsheet format) or GeoJSON (geospatial data format).
        GeoJSON preserves geometry while CSV is better for tabular analysis.
      </p>
      <tui-textfield tuiChevron   [stringify]="formatStringify"
      [tuiTextfieldCleaner]="true">
        <input tuiSelect        formControlName="format" />
        <tui-data-list *tuiTextfieldDropdown>

        @for(type of exportTypes; track $index)
          {
            <button new tuiOption type="button" [value]="type.value">
            <div class="flex flex-col">
              <span class="font-medium">{{type.label}}</span>
              <span class="text-xs text-gray-500">{{type.description}}</span>
            </div>
          </button>
          }

        </tui-data-list>
      </tui-textfield>
    </div>
  </form>

<div class="p-2 flex justify-end">
<button
    class="bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded mr-2"
    (click)="onNoClick()"
  >
    Cancel
  </button>
  <button
    class="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded disabled:bg-blue-300 disabled:cursor-not-allowed"
    (click)="onYesClick()"
  >
    Export
  </button>
    </div>

  `

})
export class ExportLayerComponent {


  public readonly context = injectContext<TuiDialogContext<{format:layerExportTypes, srid:number}, void>>();

  exportTypes = [
    {
      value: 'csv',
      label: 'CSV',
      description: 'Comma-separated values for spreadsheet applications. Loses geometric data but preserves attributes.'
    },
    {
      value: 'geojson',
      label: 'GeoJSON',
      description: 'Standard geospatial format that preserves both geometry and attributes. Best for GIS applications.'
    },
  ];

    form = new FormGroup({
      format: new FormControl<layerExportTypes>('csv', [CustomValidators.required]),
      srid:new FormControl<number>(3857)
    });

      initialFormState = {
        ...this.form.getRawValue(),
        status: 'loading' as
          | 'loading'
          | 'loaded'
          | 'submitting'
          | 'success'
          | 'error',
      };

      formState = signalSlice({
        initialState: this.initialFormState,
        sources: [
          formValues(this.form),
        ],
        selectors: (state) => ({
           showSridField: () => false,
           model: () => ({format: state.format(), srid: state.srid()})
        }),
        actionSources: {
      }});

      readonly formatStringify: TuiStringHandler<layerExportTypes> = (value) => {
        const type = this.exportTypes.find(t => t.value === value);
        return type ? type.label : '';
      };

  constructor() {

        effect(() => {

          const type = this.formState.format();

          if(type == 'geojson')
          {
            this.form.controls.srid.addValidators(CustomValidators.required);
          }
          else
          {
            this.form.controls.srid.removeValidators(CustomValidators.required);
          }


        })


  }

  onNoClick(): void {
    this.context.$implicit.complete();
  }

  onYesClick(): void {
    this.context.completeWith(this.formState.model());
  }


}
